import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, NgZone, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { QaroniDialogConfig } from '@qaroni-core/types/qaroni-dialog.config';
import { AnimationOption } from '../../types/qaroni-dialog-animation-options';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class QaroniMatDialog extends MatDialog {
  private ngZone = inject(NgZone);

  customOpen<T, D, R = undefined>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config: QaroniDialogConfig<D>
  ): MatDialogRef<T, R> {
    const matDialogRef = this.open(componentOrTemplateRef, config);

    const container = document.getElementsByTagName(
      'mat-dialog-container'
    )[0] as HTMLElement;

    if (config?.animation) {
      const incomingOptions: AnimationOption = {
        keyframes: null,
        keyframeAnimationOptions: { duration: 600, easing: 'ease-in' },
      };

      const outgoingOptions: AnimationOption = {
        keyframes: null,
        keyframeAnimationOptions: { duration: 600, easing: 'ease-out' },
      };

      const wrapper = document.getElementsByClassName(
        'cdk-global-overlay-wrapper'
      )[0];

      const animate = (
        keyframes: Keyframe[] | PropertyIndexedKeyframes | null,
        options?: number | KeyframeAnimationOptions | undefined
      ) => wrapper.animate(keyframes, options);

      const _afterClosed = new Subject<R>();

      matDialogRef.afterClosed = () => _afterClosed.asObservable();

      const closeFunction = matDialogRef.close;

      let incomeKeyFrames = incomingOptions.keyframes;
      let outgoingKeyFrames = outgoingOptions.keyframes;

      if (config?.animation.to) {
        const multyMap = { LEFT: 1, RIGHT: -1, TOP: 1, BOTTOM: -1 };

        const to = config.animation.to;

        const keyFrame100: {
          [key: string]: string | number | null | undefined;
        } = {};
        const keyFrame0: { [key: string]: string | number | null | undefined } =
          {};

        keyFrame0[to] = 0;
        keyFrame100[to] =
          to === 'TOP' || to === 'BOTTOM'
            ? container.clientHeight *
                multyMap[config?.animation.to as keyof typeof multyMap] +
              'px'
            : container.clientWidth *
                multyMap[config?.animation.to as keyof typeof multyMap] +
              'px';

        incomeKeyFrames = incomeKeyFrames || [keyFrame100, keyFrame0];
        outgoingKeyFrames = outgoingKeyFrames || [keyFrame0, keyFrame100];
      }

      animate(incomeKeyFrames, incomingOptions.keyframeAnimationOptions);

      const closeHandler = (dialogResult: R) => {
        _afterClosed.next(dialogResult);
        const animation = animate(
          outgoingKeyFrames,
          outgoingOptions.keyframeAnimationOptions
        );

        animation.onfinish = () => {
          (wrapper as HTMLElement).style.display = 'none';
          this.ngZone.run(() => matDialogRef.close(dialogResult));
        };

        matDialogRef.close = closeFunction;
      };

      matDialogRef.close = (dialogResult: R) => closeHandler(dialogResult);
    }

    return matDialogRef;
  }
}
